import Button from '@components/Button';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { ProcessedMenuItem } from '.';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { Dispatch, SetStateAction } from 'react';
import { getDynamicText } from '../EventPosterSectionMenu';
import { SPEAKER_EMAIL, SPONSOR_EMAIL } from '@utils/constants';
import { useTranslation } from 'react-i18next';

export const EventPosterSectionSubMenuItem = ({
  subItem,
  isPrimary,
  isButton,
  sluggedEvent,
  setHoveredIndex,
  coLocatedEvents,
}: {
  subItem: ProcessedMenuItem;
  isPrimary: boolean;
  isButton: boolean;
  sluggedEvent?: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  setHoveredIndex: Dispatch<SetStateAction<number | null>>;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const speakerSubject = t('apply-as-a-speaker-request-subject', {
    eventName: sluggedEvent?.name,
  });
  const speakerBody = t('apply-as-a-speaker-request-body', {
    eventName: sluggedEvent?.name,
  });
  const speakerEmailHref = sluggedEvent?.webinar
    ? `mailto:${SPEAKER_EMAIL}`
    : `mailto:${SPEAKER_EMAIL}?subject=${encodeURIComponent(speakerSubject)}&body=${encodeURIComponent(speakerBody)}`;

  const sponsorSubject = t('sponsorMailto.subject', {
    eventName: sluggedEvent?.name,
  });
  const sponsorBody = t('sponsorMailto.body', {
    eventName: sluggedEvent?.name,
  });
  const sponsorMailHref = sluggedEvent?.webinar
    ? `mailto:${SPONSOR_EMAIL}`
    : `mailto:${SPONSOR_EMAIL}?subject=${encodeURIComponent(sponsorSubject)}&body=${encodeURIComponent(sponsorBody)}`;

  const getMailToLink = (subItem: ProcessedMenuItem) => {
    if (subItem.type === 'apply-as-speaker') {
      return speakerEmailHref;
    }
    if (subItem.type === 'become-partner') {
      return sponsorMailHref;
    }
    return subItem.link;
  };

  if (isButton) {
    if (
      ['mailto', 'apply-as-speaker', 'become-partner'].includes(
        subItem.type ?? ''
      )
    ) {
      return (
        <a
          key={subItem.id}
          href={getMailToLink(subItem)}
          style={{ textDecoration: 'none', display: 'block' }}
          onClick={e => {
            e.stopPropagation();
            setHoveredIndex(null);
          }}
        >
          <Button
            title={subItem.title}
            backgroundColor={
              isPrimary
                ? sluggedEvent?.design?.themeColor
                : theme.color.transparent
            }
            hoverBackgroundColor={
              isPrimary ? theme.color.white : sluggedEvent?.design?.themeColor
            }
            textColor={
              isPrimary ? theme.color.white : sluggedEvent?.design?.themeColor
            }
            hoverTextColor={
              isPrimary ? sluggedEvent?.design?.themeColor : theme.color.white
            }
            border={`1px solid ${isPrimary ? theme.color.white : (sluggedEvent?.design?.themeColor ?? 'transparent')}`}
            borderRadius={theme.borderRadius.radius10}
            fontSize={theme.fontSize.size16}
            lineHeight={theme.lineHeight.height150}
            fontWeight="normal"
            padding="0.2rem 3rem"
            width="100%"
            margin="0 0 0.5rem 0"
          />
        </a>
      );
    } else {
      return (
        <div
          key={subItem.id}
          onClick={e => {
            e.stopPropagation();
            setHoveredIndex(null);
            if (subItem.type === 'external') {
              window.location.href = subItem.link;
            } else {
              window.location.href = `${window.location.origin}/events/${sluggedEvent?.slug ?? ''}${subItem.link}`;
            }
          }}
        >
          <Button
            title={subItem.title}
            backgroundColor={
              isPrimary
                ? sluggedEvent?.design?.themeColor
                : theme.color.transparent
            }
            hoverBackgroundColor={
              isPrimary ? theme.color.white : sluggedEvent?.design?.themeColor
            }
            textColor={
              isPrimary ? theme.color.white : sluggedEvent?.design?.themeColor
            }
            hoverTextColor={
              isPrimary ? sluggedEvent?.design?.themeColor : theme.color.white
            }
            border={`1px solid ${isPrimary ? theme.color.white : (sluggedEvent?.design?.themeColor ?? 'transparent')}`}
            borderRadius={theme.borderRadius.radius10}
            fontSize={theme.fontSize.size16}
            lineHeight={theme.lineHeight.height150}
            fontWeight="normal"
            padding="0.2rem 3rem"
            width="100%"
            margin="0 0 0.5rem 0"
          />
        </div>
      );
    }
  }

  // Non-button case
  return (
    <div
      key={subItem.id}
      style={{
        cursor: 'pointer',
        padding: '1rem 1.5rem',
        borderRadius: '0.5rem',
        transition: 'all 0.2s ease',
        color: theme.color.black,
      }}
      onClick={e => {
        e.stopPropagation();
        setHoveredIndex(null);

        if (
          ['mailto', 'apply-as-speaker', 'become-partner'].includes(
            subItem.type ?? ''
          )
        ) {
          window.location.href = getMailToLink(subItem);
        } else {
          if (subItem.type === 'external') {
            window.location.href = subItem.link;
          } else if (subItem.type === 'internal') {
            sluggedEvent?.slug &&
              navigate(`/events/${sluggedEvent?.slug}${subItem.link}`);
          }
        }
      }}
      onMouseEnter={e => {
        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div style={{ fontSize: '1.6rem', fontWeight: '600' }}>
        {getDynamicText(subItem.title, sluggedEvent, coLocatedEvents)}
      </div>

      {subItem.subtitle && subItem.subtitle !== subItem.title && (
        <div
          style={{
            fontSize: '1.2rem',
            fontWeight: '300',
            marginTop: '0.25rem',
            opacity: 0.8,
          }}
        >
          {getDynamicText(subItem.subtitle, sluggedEvent, coLocatedEvents)}
        </div>
      )}
    </div>
  );
};
